/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  IconAnthropic,
  IconDeepSeek,
  IconFlask,
  IconLightning,
  IconLightningFilled,
  IconClarifai,
  IconMeta,
  IconMistral,
  IconOpenAI,
  IconPerplexity,
  IconVerticalSlider,
  IconGCP,
  IconQwen,
  IconMicrosoft,
  IconOpenbmb,
  IconEssentialPlan,
  IconProfessionalPlan,
  IconEnterprisePlan,
  IconRunnerFilled,
  IconConnectFilled,
} from 'components/Icons';
import type { ComputeProvider, ComputeRegion } from 'modules/Compute/types';
import { ENTERPRISE_PLAN_ID, ESSENTIAL_PLAN_ID, PROFESSIONAL_PLAN_ID } from 'modules/Organizations/constants';
import { IconProps } from 'components/Icons/IconProps';
import { nanoid } from 'nanoid';
import { ScopeMatcher } from 'modules/Auth/hooks';
import { FeaturedFlagsUnion } from 'modules/FeaturedFlags/FeaturedFlagsContext';
import { permissionMatchers } from 'utils/scopes/permissionMatchers';
import { computeDocumentation, computeLinks } from 'modules/Compute/utils';
import { greenBadge, blueBadge } from './Badge/badge.styles';

export enum SetupComputeType {
  Basic = 'Basic',
  Advanced = 'Advanced',
}

export type NodePoolDetails = {
  providerId: ComputeProvider;
  regionId: ComputeRegion;
  instanceId: string;
  minInstances: number;
  maxInstances: number;
};

export enum HomeCardId {
  NewFeatures = 'NewFeatures',
  RecentActivity = 'RecentActivity',
  TrendingModels = 'TrendingModels',
  Quickstart = 'Quickstart',
  Compute = 'Compute',
  MyCompute = 'MyCompute',
  Docs = 'Docs',
  MyApps = 'MyApps',
  MyModels = 'MyModels',
  MyWorkflows = 'MyWorkflows',
  MyDatasets = 'MyDatasets',
  MyModules = 'MyModules',
}

// export const HomeCardIds = Object.values(HomeCardId);
export const HomeCardIds: HomeCardId[] = [
  HomeCardId.NewFeatures,
  HomeCardId.RecentActivity,
  HomeCardId.TrendingModels,
  HomeCardId.Compute,
  HomeCardId.MyCompute,
  HomeCardId.Quickstart,
  HomeCardId.Docs,
  HomeCardId.MyApps,
  HomeCardId.MyModels,
  HomeCardId.MyWorkflows,
  HomeCardId.MyDatasets,
  HomeCardId.MyModules,
];

export const RESOURCES_PER_PAGE_TABLE = 5;

export const mapIdToTitle = {
  [HomeCardId.RecentActivity]: 'Your Recent Activity',
  [HomeCardId.Compute]: 'Set Up Compute',
  [HomeCardId.MyCompute]: 'Your Compute',
  [HomeCardId.NewFeatures]: 'New Features',
  [HomeCardId.TrendingModels]: 'Trending AI models',
  [HomeCardId.Quickstart]: 'Quickstart for Developers',
  [HomeCardId.Docs]: 'Learn & Build with AI',
  [HomeCardId.MyApps]: 'My Apps',
  [HomeCardId.MyModels]: 'My Models',
  [HomeCardId.MyWorkflows]: 'My Workflows',
  [HomeCardId.MyDatasets]: 'My Datasets',
  [HomeCardId.MyModules]: 'My Modules',
};

export type TrendingModel = {
  icon: React.FC<IconProps>;
  title: string;
  body: string;
  modelId: string;
  userId: string;
  appId: string;
  modelVersionId: string;
  supportedComputeTypes: SetupComputeType[];
  modelData?: CF.API.Models.Model;
};

export const UserIdToIconMapping = {
  anthropic: IconAnthropic,
  perplexityai: IconPerplexity,
  mistralai: IconMistral,
  openai: IconOpenAI,
  meta: IconMeta,
  clarifai: IconClarifai,
  deepseekai: IconDeepSeek,
  'deepseek-ai': IconDeepSeek,
  deepseek: IconDeepSeek,
  gcp: IconGCP,
  llama: IconMeta,
  microsoft: IconMicrosoft,
  openbmb: IconOpenbmb,
  qwen: IconQwen,
};

export function randomSlug() {
  return nanoid(4).replace(/-|_/g, '');
}

export function getNewPATDescription(selectedCompute: SetupComputeType) {
  return `compute-${selectedCompute}-PAT-${randomSlug()}`;
}

/**
 * DON'T USE IT DIRECTLY. USE `useTrendingModels()` hook INSTEAD.
 */
export const TrendingModelFakeData: TrendingModel[] = [
  /**
   * Text to Text
   */
  {
    // https://clarifai.com/deepseek-ai/deepseek-chat/models/DeepSeek-R1-Distill-Qwen-7B
    icon: IconDeepSeek,
    title: 'DeepSeek-R1-Distill-Qwen-7B',
    body: 'DeepSeek-R1-Distill-Qwen-7B is a 7B-parameter dense model distilled from DeepSeek-R1 based on Qwen-7B.',
    modelId: 'DeepSeek-R1-Distill-Qwen-7B',
    userId: 'deepseek-ai',
    appId: 'deepseek-chat',
    modelVersionId: '16762f9f44e54bb78d3a3cd757b0cce2',
    supportedComputeTypes: [SetupComputeType.Basic, SetupComputeType.Advanced],
  },
  {
    // https://clarifai.com/meta/Llama-3/models/Llama-3_2-3B-Instruct
    icon: IconMeta,
    title: 'Llama-3.2-3B-Instruct',
    body: 'Llama 3.2 (1B) is a multilingual, instruction-tuned LLM by Meta, optimized for dialogue, retrieval, and summarization.',
    modelId: 'Llama-3_2-3B-Instruct',
    userId: 'meta',
    appId: 'Llama-3',
    modelVersionId: 'd03ace6b869b46e18ecf47b23ad8fd2a',
    supportedComputeTypes: [SetupComputeType.Basic, SetupComputeType.Advanced],
  },
  {
    // https://clarifai.com/microsoft/text-generation/models/phi-4-mini-instruct
    icon: () => <IconMicrosoft size={24} />,
    title: 'Phi-4-mini-instruct',
    body: 'Phi-4-mini-instruct is a lightweight open model from the Phi-4 family, optimized for reasoning with high-quality data.',
    modelId: 'phi-4-mini-instruct',
    userId: 'microsoft',
    appId: 'text-generation',
    modelVersionId: '1375f60fde1548ea9f105e5eef25bca9',
    supportedComputeTypes: [SetupComputeType.Basic, SetupComputeType.Advanced],
  },
  {
    // https://clarifai.com/openbmb/miniCPM/models/MiniCPM3-4B
    icon: () => <IconOpenbmb size={24} />,
    title: 'MiniCPM3-4B',
    body: 'MiniCPM3-4B is the 3rd generation of MiniCPM series.',
    modelId: 'MiniCPM3-4B',
    userId: 'openbmb',
    appId: 'miniCPM',
    modelVersionId: 'd03ed9babbc24499a563ca1c8cd9c1d2',
    supportedComputeTypes: [SetupComputeType.Basic, SetupComputeType.Advanced],
  },
  {
    // https://clarifai.com/gcp/generate/models/gemma-3-4b-it (also Multimodal to Text)
    icon: IconGCP,
    title: 'gemma-3-4b-it',
    body: 'Gemma 3 (4B) is a multilingual, multimodal open model by Google, handling text and image inputs with a 128K context window.',
    modelId: 'gemma-3-4b-it',
    userId: 'gcp',
    appId: 'generate',
    modelVersionId: '39d6a824e18f463496ea3253da7f4c1a',
    supportedComputeTypes: [SetupComputeType.Basic, SetupComputeType.Advanced],
  },
  {
    // https://clarifai.com/microsoft/text-generation/models/phi-4
    icon: () => <IconMicrosoft size={24} />,
    title: 'phi-4',
    body: 'Phi-4 is a state-of-the-art open model trained on high-quality synthetic, public, and academic data for advanced reasoning.',
    modelId: 'phi-4',
    userId: 'microsoft',
    appId: 'text-generation',
    modelVersionId: '32f4dd03440b41d0866bfe5d2bad5775',
    supportedComputeTypes: [SetupComputeType.Advanced],
  },
  {
    // https://clarifai.com/qwen/qwenCoder/models/Qwen2_5-Coder-7B-Instruct-vllm
    icon: () => <IconQwen size={24} />,
    title: 'Qwen2.5-7B-Coder-Instruct',
    body: 'Qwen2.5-Coder is a code-specific LLM series (0.5B - 32B) with improved code generation, reasoning, and fixing.',
    modelId: 'Qwen2_5-Coder-7B-Instruct-vllm',
    userId: 'qwen',
    appId: 'qwenCoder',
    modelVersionId: 'b12e65a5f6b54b07a7727007aabc31a1',
    supportedComputeTypes: [SetupComputeType.Advanced],
  },
  {
    // https://clarifai.com/qwen/qwenLM/models/QwQ-32B-AWQ
    icon: () => <IconQwen size={24} />,
    title: 'QwQ-32B-AWQ',
    body: 'QwQ is the reasoning model of the Qwen series, designed for enhanced problem-solving and downstream task performance.',
    modelId: 'QwQ-32B-AWQ',
    userId: 'qwen',
    appId: 'qwenLM',
    modelVersionId: '7c92fcc7a6ea48c2bc982c104e8e49c9',
    supportedComputeTypes: [SetupComputeType.Advanced],
  },
  /**
   * Multimodal to Text
   */
  {
    // https://clarifai.com/qwen/qwen-VL/models/Qwen2_5-VL-7B-Instruct
    icon: () => <IconQwen size={24} />,
    title: 'Qwen2.5-VL-7B-Instruct',
    body: 'Qwen2.5-VL is a vision-language model designed for AI agents, finance, and commerce.',
    modelId: 'Qwen2_5-VL-7B-Instruct',
    userId: 'qwen',
    appId: 'qwen-VL',
    modelVersionId: '68d3cda2f91f43f29e4a2291acd9dca7',
    supportedComputeTypes: [SetupComputeType.Advanced],
  },
  {
    // https://clarifai.com/openbmb/miniCPM/models/MiniCPM-o-2_6-language
    icon: () => <IconOpenbmb size={24} />,
    title: 'MiniCPM-o-2.6',
    body: 'MiniCPM-o-2d6-language is the latest series of end-side multimodal LLMs (MLLMs) ungraded from MiniCPM-V.',
    modelId: 'MiniCPM-o-2_6-language',
    userId: 'openbmb',
    appId: 'miniCPM',
    modelVersionId: 'dd6b5c76238840d2b28d088bef9b149a',
    supportedComputeTypes: [SetupComputeType.Advanced],
  },
];

export const SetupComputeMockData = [
  {
    id: 'basic-compute',
    icon: <IconFlask size={24} />,
    iconSmall: <IconFlask size={16} />,
    title: 'Basic Compute',
    body: 'Best for development and quick tests with reliable, low-cost performance.',
    footer: 'SELECT BASIC',
    badge: 'Recommended',
    badgeClassName: greenBadge,
    setupComputeType: SetupComputeType.Basic,
    requiredScopes: [permissionMatchers.computeCluster.add, permissionMatchers.nodepools.add, permissionMatchers.deployments.add] as ScopeMatcher[],
    requiredFeaturedFlags: ['ComputeOrchestration', 'ComputeResourceSmall'] as FeaturedFlagsUnion[],
    nodepoolDetails: {
      providerId: 'aws',
      regionId: 'us-east-1',
      instanceId: 'g5.2xlarge',
      minInstances: 0,
      maxInstances: 1,
    } as NodePoolDetails,
  },
  {
    id: 'advanced-compute',
    icon: <IconLightningFilled size={24} color="#344054" />,
    iconSmall: <IconLightning size={16} color="#344054" />,
    title: 'Advanced Compute',
    body: 'Best for large-scale production inference or training of complex models.',
    footer: 'SELECT ADVANCED',
    setupComputeType: SetupComputeType.Advanced,
    requiredScopes: [permissionMatchers.computeCluster.add, permissionMatchers.nodepools.add, permissionMatchers.deployments.add] as ScopeMatcher[],
    requiredFeaturedFlags: ['ComputeOrchestration', 'ComputeResourceMedium'] as FeaturedFlagsUnion[],
    nodepoolDetails: {
      providerId: 'aws',
      regionId: 'us-east-1',
      instanceId: 'g6e.xlarge',
      minInstances: 0,
      maxInstances: 1,
    } as NodePoolDetails,
  },
  {
    id: 'create-cluster',
    icon: <IconVerticalSlider size={24} />,
    iconSmall: <IconVerticalSlider size={16} />,
    title: 'Create your own',
    body: 'Select the best hardware, scaling, and region for your needs.',
    footer: 'CREATE A CLUSTER',
    footerLink: '/compute/create',
  },
  {
    id: 'connect-compute',
    icon: <IconConnectFilled size={24} />,
    iconSmall: <IconConnectFilled size={16} />,
    badge: 'New',
    badgeClassName: blueBadge,
    title: 'Connect your own compute',
    body: 'Use your AWS, GCP, or Azure setup to run models with enterprise-grade performance.',
    footer: 'CONTACT SUPPORT',
    footerLink: computeLinks.contactUs,
    isExternalLink: true,
  },
  {
    id: 'connect-runner',
    icon: <IconRunnerFilled size={24} />,
    iconSmall: <IconRunnerFilled size={16} />,
    badge: 'New',
    badgeClassName: blueBadge,
    title: 'Connect a Local Runner',
    body: 'Run models directly on your machine with ultra-low latency and complete data privacy.',
    footer: 'CONNECT A RUNNER',
    footerLink: computeDocumentation.localRunnersQuickstart,
  },
];

export const BasicInstance = SetupComputeMockData.find((data) => data.setupComputeType === SetupComputeType.Basic)!;
export const AdvancedInstance = SetupComputeMockData.find((data) => data.setupComputeType === SetupComputeType.Advanced)!;

type PartialText =
  | {
      text: string;
      type?: 'bold';
    }
  | string;

export type Text = PartialText[] | string;

type Link = {
  label: string;
  url: string;
};

export type Plan = {
  id: string;
  name: string;
  description: Text;
  dollarPricePerMonth: number;
  features: Text[];
  moreFeaturesLink?: Link;
  featuresListHeading?: string;
};

export type TBillingPlansData = {
  plans: Plan[];
  enterprisePlan: {
    description: Text;
    links: Link[];
  };
};

export type TBillingPlansDataLD =
  | TBillingPlansData
  | {
      plans: Plan[];
    }
  | undefined;

export const BillingPlanIconMapping = {
  [ESSENTIAL_PLAN_ID]: IconEssentialPlan,
  [PROFESSIONAL_PLAN_ID]: IconProfessionalPlan,
  [ENTERPRISE_PLAN_ID]: IconEnterprisePlan,
};
